// client/src/components/Greeting.js
/**
 * Resilient greeting banner with rich formatting options.
 *
 * Props:
 *  - user?: object
 *  - subtle?: boolean                // lighter background (default: false)
 *  - variant?: 'banner'|'inline'     // layout style (default: 'banner')
 *  - size?: 'xs'|'sm'|'md'|'lg'|'xl' // font sizing (default: 'md')
 *  - weight?: number|string          // font weight (default: 600)
 *  - align?: 'left'|'center'|'right' // text alignment (default: 'left')
 *  - emoji?: boolean                 // show 👋 (default: true)
 *  - background?: string             // override bg css color
 *  - color?: string                  // text color
 *  - compact?: boolean               // tighter padding (default: false)
 *  - className?: string
 *  - style?: React.CSSProperties
 */

import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../lib/api';

// ───────────────────────── helpers ─────────────────────────

const readCachedUser = () => {
  try { return JSON.parse(sessionStorage.getItem('user') || 'null'); }
  catch { return null; }
};

const writeCachedUser = (u) => {
  try {
    sessionStorage.setItem('user', JSON.stringify(u));
    window.dispatchEvent(new Event('chit:user-updated'));
  } catch {}
};

const hasNameFields = (u) =>
  !!(u?.given_name || u?.first_name || u?.firstName ||
     u?.surname || u?.last_name || u?.lastName || u?.display_name || u?.name);

const pickName = (u) => {
  if (!u) return 'there';
  const gn = u.given_name ?? u.first_name ?? u.firstName ?? u.givenName ?? '';
  const sn = u.surname ?? u.last_name ?? u.lastName ?? '';
  const full = [gn, sn].filter(Boolean).join(' ').trim();
  if (full) return full;
  if (u.display_name) return u.display_name;
  if (u.name) return u.name;
  if (u.email) return String(u.email).split('@')[0];
  return 'there';
};

const greetForZone = (zone) => {
  const now = new Date();
  try {
    if (zone) {
      const parts = new Intl.DateTimeFormat('en-AU', { timeZone: zone, hour: 'numeric', hour12: false })
        .formatToParts(now);
      const h = Number(parts.find(p => p.type === 'hour')?.value || '12');
      if (h < 12) return 'Good morning';
      if (h < 18) return 'Good afternoon';
      return 'Good evening';
    }
  } catch {}
  const h = now.getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
};

const SIZE_TO_PX = { xs: 13, sm: 15, md: 17, lg: 20, xl: 24 };
const GAP_FOR_SIZE = { xs: 6, sm: 6, md: 8, lg: 10, xl: 12 };
const PAD_Y = {
  normal: { xs: 6, sm: 8, md: 10, lg: 12, xl: 14 },
  compact: { xs: 2, sm: 4, md: 6, lg: 8, xl: 10 },
};
const PAD_X = {
  normal: { xs: 8, sm: 10, md: 12, lg: 14, xl: 16 },
  compact: { xs: 4, sm: 6, md: 8, lg: 10, xl: 12 },
};

// ───────────────────────── component ─────────────────────────

export default function Greeting({
  user,
  subtle = false,
  variant = 'banner',
  size = 'md',
  weight = 600,
  align = 'left',
  emoji = true,
  background,
  color,
  compact = false,
  className = '',
  style,
}) {
  // seed from prop or cache so we don't flicker
  const seeded = useMemo(() => user || readCachedUser(), [user]);
  const [me, setMe] = useState(seeded);

  // keep in sync with prop updates
  useEffect(() => {
    if (user) {
      setMe(user);
      writeCachedUser(user);
    }
  }, [user]);

  // update when cache changes (other tabs / app-level updates)
  useEffect(() => {
    const onUpdate = () => setMe(readCachedUser() || null);
    window.addEventListener('chit:user-updated', onUpdate);
    window.addEventListener('storage', onUpdate);
    return () => {
      window.removeEventListener('chit:user-updated', onUpdate);
      window.removeEventListener('storage', onUpdate);
    };
  }, []);

  // hydrate profile after refresh: prefer /api/users/me (has given_name/surname),
  // fall back to /api/me → /api/users/:id
  useEffect(() => {
    let cancelled = false;

    const hydrate = async () => {
      try {
        // If we already have proper name fields, no need to fetch.
        if (hasNameFields(me)) return;

        // 1) Try full profile
        let r = await apiFetch('/api/users/me');
        if (r.ok) {
          const u = await r.json();
          if (!cancelled && u) { writeCachedUser(u); setMe(u); }
          return;
        }

        // 2) Fallback: session me
        r = await apiFetch('/api/me');
        if (!r.ok) return;
        const m = await r.json();
        const sessionUser = m?.user ?? m;
        if (!sessionUser) return;

        // If session already has names, use it; else fetch by id
        if (hasNameFields(sessionUser)) {
          if (!cancelled) { writeCachedUser(sessionUser); setMe(sessionUser); }
          return;
        }

        if (sessionUser.id) {
          const r2 = await apiFetch(`/api/users/${sessionUser.id}`);
          if (r2.ok) {
            const u2 = await r2.json();
            if (!cancelled && u2) { writeCachedUser(u2); setMe(u2); }
          } else if (!cancelled) {
            writeCachedUser(sessionUser);
            setMe(sessionUser);
          }
        }
      } catch {
        // ignore – show generic greeting
      }
    };

    hydrate();
    return () => { cancelled = true; };
    // run when we don't have name info; otherwise skip
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.id, !!hasNameFields(me)]);

  // content
  const hello = greetForZone(me?.time_zone || me?.timezone);
  const name = pickName(me);

  // styling
  const px = SIZE_TO_PX[size] ?? SIZE_TO_PX.md;
  const gap = GAP_FOR_SIZE[size] ?? GAP_FOR_SIZE.md;
  const padY = (compact ? PAD_Y.compact : PAD_Y.normal)[size] ?? 8;
  const padX = (compact ? PAD_X.compact : PAD_X.normal)[size] ?? 10;

  const defaultBg = subtle || variant === 'inline' ? 'transparent' : 'var(--surface, #f6f8fb)';

  const baseStyle = {
    display: 'flex',
    alignItems: 'center',
    gap,
    padding: `${padY}px ${padX}px`,
    borderRadius: variant === 'banner' ? 12 : 0,
    background: background ?? defaultBg,
    fontSize: px,
    fontWeight: weight,
    color: color ?? 'inherit',
    textAlign: align,
    justifyContent: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start',
  };

  return (
    <div className={`greeting ${className}`.trim()} style={{ ...baseStyle, ...style }}>
      {emoji && <span role="img" aria-label="wave">👋</span>}
      <span>{hello}, {name}!</span>
    </div>
  );
}
